import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Share2, ShoppingCart, Eye, Star } from 'lucide-react';
import { useStore } from '../store/useStore';
export interface Product {
  id: number | string;
  name: string;
  price: string;
  salePrice?: string;
  image: string;
  collection?: string;
  rating?: number;
}
interface ProductCardProps {
  product: Product;
}
export function ProductCard({
  product
}: ProductCardProps) {
  const {
    addToCart,
    openQuickView
  } = useStore();
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="group bg-white flex flex-col h-full" data-id="element-283">
      <div className="relative aspect-[4/3] overflow-hidden mb-2 md:mb-3 bg-gray-50" data-id="element-284">
        <Link to={`/product/${product.id}`} className="block w-full h-full" data-id="element-285">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-286" />
          {/* Overlay Background (Clickable) */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-287" />
        </Link>

        {/* Collection Label */}
        {product.collection && <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-white/90 backdrop-blur-sm text-black text-[8px] md:text-[10px] uppercase tracking-widest px-1.5 md:px-2 py-0.5 md:py-1 font-bold border border-gray-100 pointer-events-none" data-id="element-288">
            {product.collection}
          </div>}

        {/* Sale Tag */}
        {product.salePrice && <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-gold text-black text-[8px] md:text-[10px] uppercase tracking-widest px-1.5 md:px-2 py-0.5 md:py-1 font-bold pointer-events-none" data-id="element-289">
            Sale
          </div>}

        {/* Overlay Controls (Action Buttons) */}
        <div className="absolute top-4 right-4 flex flex-col items-end space-y-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-290">
          <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} className="bg-white p-2 rounded-full hover:bg-gold hover:text-white transition-colors shadow-lg pointer-events-auto" data-id="element-291">
            <Heart size={16} data-id="element-292" />
          </button>
          <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} className="bg-white p-2 rounded-full hover:bg-gold hover:text-white transition-colors shadow-lg pointer-events-auto" data-id="element-293">
            <Share2 size={16} data-id="element-294" />
          </button>
        </div>

        {/* Quick View Button - Visible on mobile as an icon, full bar on desktop hover */}
        <div className="absolute bottom-0 left-0 right-0 translate-y-full lg:group-hover:translate-y-0 transition-transform duration-300 hidden lg:block" data-id="element-295">
          <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          openQuickView(product);
        }} className="w-full bg-black/90 text-white py-3 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-gold transition-colors" data-id="element-296">
            <Eye size={14} data-id="element-297" /> Quick View
          </button>
        </div>

        {/* Mobile Quick View Icon */}
        <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        openQuickView(product);
      }} className="lg:hidden absolute bottom-2 right-2 bg-white/90 p-2 rounded-full shadow-md text-black" data-id="element-298">
          <Eye size={14} data-id="element-299" />
        </button>
      </div>

      <div className="flex flex-col flex-grow px-1 md:px-0" data-id="element-300">
        <div className="flex justify-between items-start mb-1" data-id="element-301">
          <h3 className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-gray-900 group-hover:text-gold transition-colors line-clamp-1" data-id="element-302">
            {product.name}
          </h3>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-0.5 md:gap-1 mb-1.5 md:mb-2" data-id="element-303">
          {[...Array(5)].map((_, i) => <Star key={i} size={8} md:size={10} className={i < (product.rating || 5) ? 'fill-gold text-gold' : 'text-gray-200'} data-id="element-304" />)}
        </div>

        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3" data-id="element-305">
          {product.salePrice ? <>
              <span className="text-gold font-bold text-xs md:text-base" data-id="element-306">
                {product.salePrice}
              </span>
              <span className="text-gray-400 line-through text-[10px] md:text-xs" data-id="element-307">
                {product.price}
              </span>
            </> : <span className="text-black font-medium text-xs md:text-base" data-id="element-308">
              {product.price}
            </span>}
        </div>

        <button onClick={e => {
        e.preventDefault();
        addToCart(product);
      }} className="mt-auto w-full border border-black py-2 md:py-3 text-[8px] md:text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 md:gap-2 hover:bg-black hover:text-white transition-all duration-300 active:scale-95" data-id="element-309">
          <ShoppingCart size={12} md:size={14} data-id="element-310" /> Add to Cart
        </button>
      </div>
    </motion.div>;
}